import React, { useState } from 'react';
import {
    Globe2, Network, Award, Calendar, MapPin, Users, BookOpen,
    Shield, Lightbulb, Database, Cpu, Target, ChevronRight,
    CheckCircle, Zap, TrendingUp, Wheat, Heart, GraduationCap,
    Building2, FileText, Handshake, Star, ExternalLink, ArrowRight,
    Landmark, Scale, FlaskConical, Megaphone
} from 'lucide-react';

// VC Report Section: Official Launch Event
export function OfficialLaunchSection() {
    return (
        <div className="relative overflow-hidden bg-gradient-to-br from-blue-700 via-blue-800 to-blue-900 py-20">
            {/* Grid background */}
            <div
                className="absolute inset-0 opacity-20"
                style={{
                    backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' xmlns='http://www.w3.org/2000/svg'%3E%3Cdefs%3E%3Cpattern id='grid' width='60' height='60' patternUnits='userSpaceOnUse'%3E%3Cpath d='M 60 0 L 0 0 0 60' fill='none' stroke='rgba(255,255,255,0.15)' stroke-width='1'/%3E%3C/pattern%3E%3C/defs%3E%3Crect width='100%25' height='100%25' fill='url(%23grid)'/%3E%3C/svg%3E")`
                }}
            />

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">


                <div className="text-center mb-12">
                    <h2 className="text-4xl md:text-5xl font-bold text-white leading-tight mb-4">
                        Official Launch of AICEX-Africa
                    </h2>
                    <p className="text-xl text-blue-100 max-w-3xl mx-auto leading-relaxed">
                        A landmark moment marking Kenya's commitment to leading Africa's AI transformation
                    </p>
                </div>

                {/* Official Launch Photo */}
                <div className="mb-10">
                    <figure className="overflow-hidden rounded-3xl border-4 border-white/20 shadow-2xl">
                        <img
                            src="/vizafrica-2025.png"
                            alt="VizAfrica 2025 launch of AICEX-Africa"
                            className="w-full object-cover max-h-[520px]"
                        />
                        <figcaption className="bg-white/10 backdrop-blur-sm border-t border-white/20 px-6 py-4">
                            <p className="text-blue-100 text-sm leading-relaxed text-center">
                                <span className="font-semibold text-white">L-R seated:</span> Patrick Wakhu (Director AICAD), Dr TShiamo Mushegwa (Director AOSP),
                                Prof Muliaro Wafula (Lead Scientist AICEX-Africa, JKUAT), Ambassador Philip Thigo (Special Envoy on Technology to H.E. the President
                                of the Republic of Kenya), Professor Victoria Wambui Ngumi (Vice Chancellor of JKUAT) and Professor Christopher Chepken
                                (DVC, Open University of Kenya)
                            </p>
                        </figcaption>
                    </figure>
                </div>

                <div className="grid lg:grid-cols-2 gap-10 items-start">
                    {/* Left — Launch details */}
                    <div className="space-y-6">
                        <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-3xl p-8">
                            <div className="flex items-center gap-3 mb-6">
                                <div className="bg-white/20 rounded-xl p-3">
                                    <Award className="w-6 h-6 text-white" />
                                </div>
                                <h3 className="text-xl font-bold text-white">VizAfrica 2025 International Conference</h3>
                            </div>

                            <div className="space-y-4">
                                <div className="flex items-center gap-3 text-blue-100">
                                    <Calendar className="w-5 h-5 text-blue-300 flex-shrink-0" />
                                    <span className="font-medium">3 December 2025</span>
                                </div>
                                <div className="flex items-center gap-3 text-blue-100">
                                    <MapPin className="w-5 h-5 text-blue-300 flex-shrink-0" />
                                    <span>Launched at the VizAfrica 2025 International Conference</span>
                                </div>
                                <div className="flex items-start gap-3 text-blue-100">
                                    <Users className="w-5 h-5 text-blue-300 flex-shrink-0 mt-0.5" />
                                    <span>Launched by <strong className="text-white">Ambassador Philip Thigo</strong>, Special Envoy on Technology to H.E. the President of the Republic of Kenya, in the presence of <strong className="text-white">Professor Victoria Wambui Ngumi</strong>, Vice Chancellor of JKUAT</span>
                                </div>
                            </div>
                        </div>

                        {/* Quote */}
                        <div className="bg-white/5 backdrop-blur-sm border border-white/20 rounded-2xl p-6">
                            <div className="flex items-start gap-4">
                                <div className="w-1 bg-yellow-400 rounded-full self-stretch flex-shrink-0" />
                                <div>
                                    <p className="text-white/90 italic leading-relaxed text-lg mb-3">
                                        "Capacity-building is crucial for the safe and responsible adoption of AI.
                                        We need collaborations to strengthen institutional readiness and close the
                                        AI divide in the Global South."
                                    </p>
                                    <p className="text-sm text-blue-200 font-semibold">
                                        — Ambassador Philip Thigo, Kenya's Special Envoy on Technology
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Right — Notable attendees / key figures */}
                    <div className="space-y-4">
                        <h3 className="text-white font-bold text-lg mb-4 flex items-center gap-2">
                            <Users className="w-5 h-5 text-blue-300" />
                            Dignitaries Present at the Launch
                        </h3>

                        {[
                            {
                                name: 'Ambassador Philip Thigo',
                                role: 'Special Envoy on Technology to H.E. the President of Kenya',
                                note: 'Officiated the launch'
                            },
                            {
                                name: 'Prof. Victoria Wambui Ngumi',
                                role: 'Vice Chancellor, JKUAT',
                                note: 'Host institution leader'
                            },
                            {
                                name: 'Prof. Muliaro Wafula',
                                role: 'Lead Scientist AICEX-Africa, JKUAT',
                                note: 'Centre Director'
                            },
                            {
                                name: 'Patrick Wakhu',
                                role: 'Director, AICAD',
                                note: 'Host institution'
                            },
                            {
                                name: 'Dr. TShiamo Mushegwa',
                                role: 'Director, AOSP',
                                note: 'Regional platform'
                            },
                            {
                                name: 'Prof. Christopher Chepken',
                                role: 'DVC, Open University of Kenya',
                                note: 'Partner institution'
                            }
                        ].map((person, idx) => (
                            <div key={idx} className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl p-4 hover:bg-white/15 transition-all duration-300">
                                <div className="flex items-start gap-3">
                                    <div className="bg-blue-500/30 rounded-full w-9 h-9 flex items-center justify-center flex-shrink-0 mt-0.5">
                                        <span className="text-white font-bold text-sm">{person.name.charAt(0)}</span>
                                    </div>
                                    <div>
                                        <p className="font-semibold text-white text-sm">{person.name}</p>
                                        <p className="text-blue-200 text-xs leading-relaxed">{person.role}</p>
                                        <span className="inline-block mt-1 text-xs bg-white/10 text-blue-100 px-2 py-0.5 rounded-full">{person.note}</span>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>


            </div>
        </div>
    );
}

// VC Report Section: AICDN Geneva Launch
export function AICDNLaunchSection() {
    const flagshipProjects = [
        {
            icon: GraduationCap,
            title: 'Coordinated AI Training Programme',
            color: 'blue',
            items: [
                'Ethics and responsible AI',
                'AI policy and governance'
            ]
        },
        {
            icon: BookOpen,
            title: 'AI Education Programme',
            color: 'indigo',
            items: [
                'Ethics and responsible AI',
                'AI policy and governance',
                'Capacity development for policymakers, public servants, students, and researchers'
            ]
        },
        {
            icon: FileText,
            title: 'AI Policy Blueprint',
            color: 'purple',
            items: [
                'National AI Centre policy framework',
                'Regulatory guidance for member states',
                'Governance best practices'
            ]
        }
    ];

    return (
        <div className="bg-white py-20">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                {/* Header */}
                <div className="text-center mb-14">
                    <div className="inline-flex items-center gap-2 bg-blue-50 border border-blue-200 rounded-full px-5 py-2 mb-6">
                        <Globe2 className="w-4 h-4 text-blue-600" />
                        <span className="text-sm font-semibold text-blue-700 uppercase tracking-wide">5 July 2026 · Geneva, Switzerland</span>
                    </div>
                    <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4 leading-tight">
                        UN-Supported AI Capacity<br />Development Network (AICDN)
                    </h2>
                    <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
                        AICEX-Africa is a <strong>founding member</strong> of the global AICDN, officially launched at the
                        United Nations offices in Geneva with support from the UN Office for Digital and Emerging Technologies (UN-ODET)
                    </p>
                    <div className="mt-6">
                        <a
                            href="https://aicdn.global/"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold px-7 py-3 rounded-full shadow-md hover:shadow-xl transition-all duration-300 hover:-translate-y-0.5"
                        >
                            <Globe2 className="w-4 h-4" />
                            Visit aicdn.global
                            <ExternalLink className="w-4 h-4 opacity-80" />
                        </a>
                    </div>
                </div>

                {/* Context cards */}
                <div className="grid md:grid-cols-2 gap-8 mb-14">
                    <div className="bg-gradient-to-br from-blue-50 to-blue-100 border border-blue-200 rounded-3xl p-8 hover:shadow-xl transition-all duration-300">
                        <Network className="w-10 h-10 text-blue-600 mb-4" />
                        <h3 className="text-xl font-bold text-gray-900 mb-3">About AICDN</h3>
                        <p className="text-gray-700 leading-relaxed mb-4">
                            A global network of AI Centres of Excellence established in response to the
                            <strong> UN Global Digital Compact</strong> and the UN Secretary-General's recommendations
                            on innovative financing and international cooperation for AI capacity development.
                        </p>
                        <p className="text-gray-700 leading-relaxed">
                            The network promotes <strong>Open Science</strong>, responsible AI, knowledge sharing,
                            and both South-South and North-South cooperation.
                        </p>
                    </div>

                    <div className="bg-gradient-to-br from-indigo-50 to-indigo-100 border border-indigo-200 rounded-3xl p-8 hover:shadow-xl transition-all duration-300">
                        <Target className="w-10 h-10 text-indigo-600 mb-4" />
                        <h3 className="text-xl font-bold text-gray-900 mb-3">AICDN's Overarching Objective</h3>
                        <p className="text-gray-700 leading-relaxed mb-4">
                            To <strong>reduce the global AI capacity gap</strong> by promoting collaboration among
                            governments, universities, research institutions, industry, and development partners —
                            ensuring that no country is left behind in the AI revolution.
                        </p>
                        <div className="mt-4 space-y-2">
                            {[
                                'Computing resources & datasets',
                                'Curricula & training programmes',
                                'Strategic partnerships',
                                'Open Science principles & FAIR data'
                            ].map((item, i) => (
                                <div key={i} className="flex items-center gap-2 text-gray-700 text-sm">
                                    <CheckCircle className="w-4 h-4 text-indigo-500 flex-shrink-0" />
                                    <span>{item}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Kenya's Role */}
                <div className="bg-gradient-to-r from-gray-900 to-blue-900 rounded-3xl p-8 md:p-10 mb-8">
                    <div className="grid md:grid-cols-2 gap-8 items-start">
                        <div>
                            <div className="inline-flex items-center gap-2 bg-white/10 border border-white/20 rounded-full px-4 py-1.5 mb-5">
                                <Landmark className="w-4 h-4 text-blue-300" />
                                <span className="text-sm text-blue-200 font-medium">Kenya's Founding Role</span>
                            </div>
                            <h3 className="text-2xl font-bold text-white mb-4">
                                JKUAT Signs the Cooperation Framework
                            </h3>
                            <p className="text-gray-300 leading-relaxed">
                                Professor Muliaro Wafula, representing both JKUAT and the Office of the Special Envoy on
                                Technology to H.E. the President of the Republic of Kenya, signed the Cooperation Framework
                                at the Geneva launch — formally positioning JKUAT among the <strong className="text-white">founding institutions
                                    of the global Network</strong>.
                            </p>
                        </div>
                        <div className="space-y-3">
                            {[
                                { label: 'Launch Date', value: '5 July 2026', icon: Calendar },
                                { label: 'Location', value: 'Geneva, Switzerland (UN Offices)', icon: MapPin },
                                { label: 'Signatory', value: 'Prof. Muliaro Wafula', icon: Users },
                                { label: 'Supporting Body', value: 'UN Office for Digital & Emerging Technologies', icon: Globe2 }
                            ].map((item, i) => (
                                <div key={i} className="flex items-center gap-4 bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl px-5 py-3">
                                    <item.icon className="w-5 h-5 text-blue-300 flex-shrink-0" />
                                    <div>
                                        <p className="text-xs text-blue-300 font-medium">{item.label}</p>
                                        <p className="text-white text-sm font-semibold">{item.value}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Geneva Photo — portrait 840×1120, show in full */}
                    <div className="mt-8 pt-8 border-t border-white/10">
                        <figure>
                            <div className="rounded-2xl overflow-hidden bg-gray-950 flex justify-center">
                                <img
                                    src="/vizafrica-2025-2.jpg"
                                    alt="Prof Muliaro Wafula and Amb Philip Thigo at AICDN launch in Geneva"
                                    className="w-full max-w-lg object-contain"
                                />
                            </div>
                            <figcaption className="mt-3 text-center">
                                <p className="text-blue-200 text-sm leading-relaxed">
                                    Prof Muliaro Wafula of JKUAT and Amb Philip Thigo, Special Envoy on Technology to H.E. the President
                                    of the Republic of Kenya, at the launch of AICDN — Geneva, Switzerland, 5th July 2026
                                </p>
                            </figcaption>
                        </figure>
                    </div>
                </div>

                {/* Cooperation Framework Photos — different aspect ratios, use object-contain */}
                <div className="mb-14">
                    <div className="grid md:grid-cols-2 gap-6">
                        {/* aicex-launch.jpg: 1080×1920 portrait */}
                        <figure className="overflow-hidden rounded-2xl border border-gray-200 shadow-lg hover:shadow-xl transition-all duration-300">
                            <div className="bg-gray-100 flex items-center justify-center" style={{ minHeight: '320px' }}>
                                <img
                                    src="/aicex-launch.jpg"
                                    alt="Signed Cooperation Frameworks at the AICDN launch in Geneva"
                                    className="w-full h-auto object-contain"
                                />
                            </div>
                        </figure>
                        {/* aicdn-aicex-launch.jpg: 1280×720 landscape */}
                        <figure className="overflow-hidden rounded-2xl border border-gray-200 shadow-lg hover:shadow-xl transition-all duration-300 flex items-center">
                            <img
                                src="/aicdn-aicex-launch.jpg"
                                alt="Signed Cooperation Frameworks at the AICDN launch in Geneva"
                                className="w-full h-auto object-contain"
                            />
                        </figure>
                    </div>
                    <figcaption className="mt-4 text-center">
                        <p className="text-gray-500 text-sm leading-relaxed">
                            Display of signed Cooperation Frameworks at the launch of AICDN in Geneva UN offices
                        </p>
                    </figcaption>
                </div>

                {/* Flagship Projects */}
                <div>
                    <div className="text-center mb-10">
                        <h3 className="text-3xl font-bold text-gray-900 mb-3">AICDN Flagship Collaborative Projects</h3>
                        <p className="text-gray-600 max-w-2xl mx-auto">
                            Three identified strategic programmes driving AICDN's global impact
                        </p>
                    </div>

                    <div className="grid md:grid-cols-3 gap-6">
                        {flagshipProjects.map((project, idx) => {
                            const colorMap = {
                                blue: { border: 'border-blue-200 hover:border-blue-400', icon: 'text-blue-600', badge: 'bg-blue-100 text-blue-700', num: 'bg-blue-600' },
                                indigo: { border: 'border-indigo-200 hover:border-indigo-400', icon: 'text-indigo-600', badge: 'bg-indigo-100 text-indigo-700', num: 'bg-indigo-600' },
                                purple: { border: 'border-purple-200 hover:border-purple-400', icon: 'text-purple-600', badge: 'bg-purple-100 text-purple-700', num: 'bg-purple-600' }
                            };
                            const c = colorMap[project.color];
                            return (
                                <div key={idx} className={`bg-white border-2 ${c.border} rounded-2xl p-7 hover:shadow-xl transition-all duration-300`}>
                                    <div className="flex items-center gap-3 mb-5">
                                        <div className={`${c.num} rounded-xl w-9 h-9 flex items-center justify-center`}>
                                            <span className="text-white text-sm font-bold">{idx + 1}</span>
                                        </div>
                                        <project.icon className={`w-7 h-7 ${c.icon}`} />
                                    </div>
                                    <h4 className="text-lg font-bold text-gray-900 mb-4">{project.title}</h4>
                                    <ul className="space-y-2">
                                        {project.items.map((item, i) => (
                                            <li key={i} className="flex items-start gap-2 text-sm text-gray-700">
                                                <ChevronRight className={`w-4 h-4 ${c.icon} flex-shrink-0 mt-0.5`} />
                                                <span>{item}</span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>
        </div>
    );
}

// VC Report Section: Organizational Structure (3 Roles)
export function OrganizationalRolesSection() {
    const roles = [
        {
            number: '01',
            title: 'Academic & Research Centre',
            subtitle: "JKUAT's Institutional AI Centre",
            icon: FlaskConical,
            color: 'blue',
            description: 'As JKUAT\'s institutional AI Centre, AICEX-Africa anchors Kenya\'s academic AI ecosystem within the School of Computing and Information Technology (SCIT), under the Department of Computing.',
            items: [
                'AI education and talent development',
                'Collaborative research programmes',
                'Innovation and entrepreneurship',
                'International academic partnerships through AICDN'
            ]
        },
        {
            number: '02',
            title: 'National AI Centre',
            subtitle: 'Kenya\'s AI Strategy Hub',
            icon: Landmark,
            color: 'indigo',
            description: 'At the national level, AICEX-Africa provides a collaborative ecosystem that supports the full breadth of Kenya\'s national digital transformation and AI implementation agenda.',
            items: [
                "Kenya's National AI Strategy implementation",
                'National Digital Economy initiatives',
                'AI policy implementation & advisory',
                'AI-driven innovation across all economic sectors',
                'Responsible, transparent, and inclusive AI adoption'
            ]
        },
        {
            number: '03',
            title: 'Regional Centre',
            subtitle: 'Africa-Wide AI Platform',
            icon: Globe2,
            color: 'purple',
            description: 'At the regional level, AICEX-Africa aligns with the Africa Open Science Platform (AOSP) East Africa Node, promoting AI capacity development across Africa using Open Science principles.',
            items: [
                'FAIR data principles (Findable, Accessible, Interoperable, Reusable)',
                'CODATA partnership as Kenya\'s National Member',
                'Regional AI collaboration and open science',
                'South-South and North-South knowledge exchange'
            ]
        }
    ];

    const colorMap = {
        blue: {
            gradient: 'from-blue-600 to-blue-700',
            border: 'border-blue-200',
            icon: 'text-blue-600',
            badge: 'bg-blue-100 text-blue-700',
            bullet: 'text-blue-500'
        },
        indigo: {
            gradient: 'from-indigo-600 to-indigo-700',
            border: 'border-indigo-200',
            icon: 'text-indigo-600',
            badge: 'bg-indigo-100 text-indigo-700',
            bullet: 'text-indigo-500'
        },
        purple: {
            gradient: 'from-purple-600 to-purple-700',
            border: 'border-purple-200',
            icon: 'text-purple-600',
            badge: 'bg-purple-100 text-purple-700',
            bullet: 'text-purple-500'
        }
    };

    return (
        <div className="bg-gray-50 py-20">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="text-center mb-14">
                    <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
                        Three Strategic Roles
                    </h2>
                    <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
                        AICEX-Africa operates at three complementary levels — enabling collaboration from
                        the classroom to the continent
                    </p>
                </div>

                <div className="space-y-8">
                    {roles.map((role, idx) => {
                        const c = colorMap[role.color];
                        return (
                            <div key={idx} className={`bg-white border ${c.border} rounded-3xl overflow-hidden hover:shadow-2xl transition-all duration-300`}>
                                <div className={`bg-gradient-to-r ${c.gradient} p-8 text-white`}>
                                    <div className="flex items-center gap-5">
                                        <div className="bg-white/20 rounded-2xl p-4">
                                            <role.icon className="w-8 h-8 text-white" />
                                        </div>
                                        <div>
                                            <div className="text-5xl font-black text-white/20 absolute right-10 top-5 hidden md:block">{role.number}</div>
                                            <span className="text-white/70 text-sm font-medium uppercase tracking-wider">{role.subtitle}</span>
                                            <h3 className="text-2xl font-bold text-white">{role.title}</h3>
                                        </div>
                                    </div>
                                </div>
                                <div className="p-8 grid md:grid-cols-2 gap-8">
                                    <p className="text-gray-700 leading-relaxed text-lg">{role.description}</p>
                                    <ul className="space-y-3">
                                        {role.items.map((item, i) => (
                                            <li key={i} className="flex items-start gap-3 text-gray-700">
                                                <CheckCircle className={`w-5 h-5 ${c.icon} flex-shrink-0 mt-0.5`} />
                                                <span>{item}</span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
}

// VC Report Section: AICDN Areas of Cooperation & Key Activities
export function AICDNCooperationSection() {
    const [activeTab, setActiveTab] = useState('cooperation');

    const cooperation = [
        { icon: Building2, title: 'Institutional AI Capacity', desc: 'Development of AI Centres of Excellence and national AI ecosystems across member states' },
        { icon: FileText, title: 'National AI Strategy Support', desc: 'Support for National AI Strategy development upon request from member governments' },
        { icon: GraduationCap, title: 'Multi-Domain AI Training', desc: 'Training covering policy, strategy, technical, commercial, and public-private partnership aspects' },
        { icon: Cpu, title: 'Foundational AI Capacity', desc: 'Development of essential AI skills and knowledge foundations across institutions' },
        { icon: Database, title: 'Resource Exchange', desc: 'Exchange of AI models, datasets, computing resources, and real-world AI use cases' },
        { icon: Zap, title: 'Translational AI', desc: 'Practical use-case engineering and deployment bridging research to real-world impact' }
    ];

    const activities = [
        { icon: Megaphone, text: 'Presentations at major international AI conferences' },
        { icon: Network, text: 'Networking regional and global AI capacity-building initiatives' },
        { icon: Scale, text: 'Building AI governance capacity for public sector institutions' },
        { icon: Database, text: 'Facilitating access to AI training resources, compute infrastructure, and datasets' },
        { icon: FlaskConical, text: 'Supporting interdisciplinary research through shared computing platforms' },
        { icon: Shield, text: 'Establishing AI regulatory sandboxes for experimentation and innovation' },
        { icon: BookOpen, text: 'Developing online AI education programmes' },
        { icon: Users, text: 'Pooling training materials and expert trainers across member institutions' },
        { icon: Star, text: 'Implementing fellowship programmes for researchers and AI practitioners' },
        { icon: Wheat, text: 'Joint development of AI implementation playbooks in Agriculture, Health, Education, Industrial Development & Environment' },
        { icon: FlaskConical, text: 'Promoting collaborative AI research across member institutions' },
        { icon: Globe2, text: 'Establishing an international pool of AI experts' }
    ];

    return (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
            <div className="text-center mb-12">
                <h2 className="text-4xl font-bold text-gray-900 mb-4">AICDN Cooperation & Activities</h2>
                <p className="text-gray-600 text-lg max-w-2xl mx-auto">
                    How the global AI Capacity Development Network drives impact through cooperation and strategic action
                </p>
            </div>

            {/* Tab Toggle */}
            <div className="flex justify-center mb-10">
                <div className="bg-gray-100 rounded-full p-1 flex gap-1">
                    <button
                        onClick={() => setActiveTab('cooperation')}
                        className={`px-6 py-2.5 rounded-full text-sm font-semibold transition-all duration-300 ${activeTab === 'cooperation'
                            ? 'bg-blue-600 text-white shadow-md'
                            : 'text-gray-600 hover:text-gray-900'
                            }`}
                    >
                        Areas of Cooperation
                    </button>
                    <button
                        onClick={() => setActiveTab('activities')}
                        className={`px-6 py-2.5 rounded-full text-sm font-semibold transition-all duration-300 ${activeTab === 'activities'
                            ? 'bg-blue-600 text-white shadow-md'
                            : 'text-gray-600 hover:text-gray-900'
                            }`}
                    >
                        Key Activities
                    </button>
                </div>
            </div>

            {/* Cooperation Tab */}
            {activeTab === 'cooperation' && (
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {cooperation.map((item, idx) => (
                        <div key={idx} className="bg-white border border-gray-200 rounded-2xl p-6 hover:border-blue-300 hover:shadow-lg transition-all duration-300">
                            <div className="bg-blue-50 rounded-xl p-3 w-fit mb-4">
                                <item.icon className="w-6 h-6 text-blue-600" />
                            </div>
                            <h4 className="text-lg font-bold text-gray-900 mb-2">{item.title}</h4>
                            <p className="text-gray-600 text-sm leading-relaxed">{item.desc}</p>
                        </div>
                    ))}
                </div>
            )}

            {/* Activities Tab */}
            {activeTab === 'activities' && (
                <div className="grid md:grid-cols-2 gap-4">
                    {activities.map((item, idx) => (
                        <div key={idx} className="flex items-start gap-4 bg-white border border-gray-200 rounded-xl p-5 hover:border-blue-300 hover:shadow-md transition-all duration-300">
                            <div className="bg-blue-50 rounded-lg p-2 flex-shrink-0">
                                <item.icon className="w-5 h-5 text-blue-600" />
                            </div>
                            <p className="text-gray-700 text-sm leading-relaxed">{item.text}</p>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

// VC Report Section: Strategic Objectives (enriched from report)
export function StrategicObjectivesSection() {
    const objectives = [
        {
            icon: GraduationCap,
            title: 'World-Class AI Education',
            desc: 'Develop internationally competitive AI education and professional training programmes that meet global standards'
        },
        {
            icon: FlaskConical,
            title: 'Multidisciplinary Research',
            desc: 'Conduct multidisciplinary AI research addressing national and regional development priorities'
        },
        {
            icon: Scale,
            title: 'Policy & Governance Leadership',
            desc: 'Support AI policy development, governance, ethics, and regulatory capacity across Kenya and Africa'
        },
        {
            icon: Database,
            title: 'Open Science & FAIR Data',
            desc: 'Promote Open Science, FAIR data principles, and trusted research environments for African scientists'
        },
        {
            icon: Handshake,
            title: 'University-Industry-Government Collaboration',
            desc: 'Strengthen three-way collaboration between universities, industry, and government for AI innovation'
        },
        {
            icon: Lightbulb,
            title: 'AI Startup Incubation',
            desc: 'Incubate AI startups and facilitate the commercialization of AI technologies with market viability'
        },
        {
            icon: Globe2,
            title: 'Regional AI Partnerships',
            desc: 'Build regional partnerships for AI knowledge exchange and collaborative research across Africa'
        },
        {
            icon: Network,
            title: 'Global AICDN Engagement',
            desc: 'Mobilize international partnerships, research grants, technology transfer and investment through AICDN'
        }
    ];

    return (
        <div className="bg-gradient-to-br from-gray-50 to-blue-50 py-20">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="text-center mb-14">
                    <h2 className="text-4xl font-bold text-gray-900 mb-4">Strategic Objectives</h2>
                    <p className="text-gray-600 text-lg max-w-2xl mx-auto">
                        As Kenya's flagship AI capacity-building centre, AICEX-Africa pursues eight transformative objectives
                    </p>
                </div>

                <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
                    {objectives.map((obj, idx) => (
                        <div key={idx} className="bg-white border border-gray-200 rounded-2xl p-6 hover:border-blue-300 hover:shadow-xl transition-all duration-300 group">
                            <div className="flex items-center gap-3 mb-4">
                                <div className="bg-blue-600 rounded-xl w-10 h-10 flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                                    <obj.icon className="w-5 h-5 text-white" />
                                </div>
                                <span className="text-3xl font-black text-gray-100 group-hover:text-blue-100 transition-colors duration-300">
                                    {String(idx + 1).padStart(2, '0')}
                                </span>
                            </div>
                            <h4 className="font-bold text-gray-900 mb-2 group-hover:text-blue-700 transition-colors duration-300">{obj.title}</h4>
                            <p className="text-gray-600 text-sm leading-relaxed">{obj.desc}</p>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}

// VC Report Section: Conclusion / Impact Statement
export function ConclusionSection() {
    return (
        <div className="relative overflow-hidden bg-gradient-to-br from-blue-700 via-blue-800 to-indigo-900 py-20">
            <div
                className="absolute inset-0 opacity-10"
                style={{
                    backgroundImage: `url("data:image/svg+xml,%3Csvg width='80' height='80' xmlns='http://www.w3.org/2000/svg'%3E%3Cdefs%3E%3Cpattern id='dots' width='80' height='80' patternUnits='userSpaceOnUse'%3E%3Ccircle cx='40' cy='40' r='2' fill='white'/%3E%3C/pattern%3E%3C/defs%3E%3Crect width='100%25' height='100%25' fill='url(%23dots)'/%3E%3C/svg%3E")`
                }}
            />
            <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 relative text-center">
                <div className="inline-flex items-center gap-2 bg-white/10 border border-white/20 rounded-full px-5 py-2 mb-8">
                    <TrendingUp className="w-4 h-4 text-blue-300" />
                    <span className="text-sm font-semibold text-blue-100 uppercase tracking-wide">JKUAT VC's Report — Conclusion</span>
                </div>

                <h2 className="text-4xl md:text-5xl font-bold text-white mb-8 leading-tight">
                    A Milestone in Africa's AI Leadership
                </h2>

                <p className="text-xl text-blue-100 leading-relaxed mb-10 max-w-4xl mx-auto">
                    The establishment of AICEX-Africa represents a major milestone in JKUAT's strategic commitment to
                    advancing Artificial Intelligence research, innovation, education, and policy leadership. Through its
                    integration into the United Nations-supported Artificial Intelligence Capacity Development Network,
                    the Centre provides Kenya with a globally connected platform for AI capacity development while
                    positioning JKUAT as a leading institution in Africa's AI ecosystem.
                </p>

                <div className="grid md:grid-cols-3 gap-6 mt-12">
                    {[
                        {
                            icon: Shield,
                            title: "Kenya's AI Strategy",
                            desc: 'Supporting implementation of national AI strategy and digital governance'
                        },
                        {
                            icon: GraduationCap,
                            title: 'Human Capital Development',
                            desc: 'Developing research excellence and fostering innovation and entrepreneurship'
                        },
                        {
                            icon: Globe2,
                            title: 'Continental Transformation',
                            desc: 'Contributing to sustainable socio-economic transformation across Kenya and Africa'
                        }
                    ].map((item, idx) => (
                        <div key={idx} className="bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl p-6 hover:bg-white/20 transition-all duration-300">
                            <item.icon className="w-8 h-8 text-blue-300 mb-3 mx-auto" />
                            <h4 className="font-bold text-white mb-2">{item.title}</h4>
                            <p className="text-blue-200 text-sm leading-relaxed">{item.desc}</p>
                        </div>
                    ))}
                </div>

                <p className="mt-10 text-blue-200 text-lg leading-relaxed max-w-3xl mx-auto">
                    Through <strong className="text-white">responsible, inclusive, and collaborative AI development</strong>,
                    AICEX-Africa is expected to play a pivotal role in positioning Africa at the forefront of the global
                    AI revolution — ensuring the continent's voice, values, and needs shape the future of AI.
                </p>
            </div>
        </div>
    );
}
